import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, RotateCcw } from "lucide-react";

// Two-way live chat. Visitor messages → /api/chat-message → owner's Telegram
// (with a #sid marker). Owner replies by hitting Reply in Telegram → delivered
// to /api/telegram-webhook → stored → this widget polls /api/chat-poll and shows
// the reply live. Thread + sid persist in localStorage so it survives reloads.
const SID_KEY = "vroum_chat_sid";
const MSGS_KEY = "vroum_chat_msgs";

function loadMsgs() {
  try { return JSON.parse(localStorage.getItem(MSGS_KEY) || "[]"); } catch { return []; }
}

export default function LiveChat() {
  const { pathname } = useLocation();
  const en = pathname.startsWith("/en");
  const T = en
    ? { btn: "Message", head: "Chat with us", greet: "Hi! 👋 Send a message — we’ll reply right here.", msg: "Type a message…", contact: "Phone / email (so we can reach you)", send: "Send", err: "Couldn’t send — try again.", reset: "New chat" }
    : { btn: "Μήνυμα", head: "Μίλα μαζί μας", greet: "Γεια! 👋 Γράψε μας — θα σου απαντήσουμε εδώ.", msg: "Γράψε μήνυμα…", contact: "Τηλέφωνο / email (για να σε βρούμε)", send: "Στείλε", err: "Δεν στάλθηκε — δοκίμασε ξανά.", reset: "Νέα συνομιλία" };

  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(loadMsgs);
  const [text, setText] = useState("");
  const [contact, setContact] = useState("");
  const [unread, setUnread] = useState(false);
  const [err, setErr] = useState(false);
  const [typing, setTyping] = useState(false);
  const sidRef = useRef(localStorage.getItem(SID_KEY) || "");
  const bodyRef = useRef(null);

  // persist + autoscroll
  useEffect(() => {
    try { localStorage.setItem(MSGS_KEY, JSON.stringify(msgs.slice(-50))); } catch {}
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, open]);

  // Timestamp of the latest owner msg we've shown (so we only fetch newer ones).
  const lastAtRef = useRef("");
  useEffect(() => {
    const init = loadMsgs();
    for (let i = init.length - 1; i >= 0; i--) if (init[i].role === "owner") { lastAtRef.current = init[i].at; break; }
    // Load the FULL saved conversation from the server (authoritative) so the
    // customer always sees their complete history, not just this device's cache.
    if (sidRef.current) {
      fetch(`/api/chat-history?sid=${encodeURIComponent(sidRef.current)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && Array.isArray(d.messages) && d.messages.length) {
            setMsgs(d.messages.map((x) => ({ role: x.role === "visitor" ? "visitor" : "owner", text: x.text, at: x.at })));
            for (let i = d.messages.length - 1; i >= 0; i--) if (d.messages[i].role !== "visitor") { lastAtRef.current = d.messages[i].at; break; }
          }
        })
        .catch(() => {});
    }
  }, []);

  // Single polling loop set up ONCE. Reads sidRef fresh each tick (so it starts
  // working the moment the first message creates a session) + polls on tab focus.
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const sid = sidRef.current;
      if (!sid) return; // poll even when tab is backgrounded so replies/badge stay live
      try {
        const after = lastAtRef.current;
        const r = await fetch(`/api/chat-poll?sid=${encodeURIComponent(sid)}${after ? `&after=${encodeURIComponent(after)}` : ""}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!stop && d.messages && d.messages.length) {
          lastAtRef.current = d.messages[d.messages.length - 1].at;
          setTyping(false);
          setMsgs((m) => [...m, ...d.messages.map((x) => ({ role: "owner", text: x.text, at: x.at }))]);
          setOpen((o) => { if (!o) setUnread(true); return o; });
        }
      } catch {}
    };
    const id = setInterval(tick, 3000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    tick();
    return () => { stop = true; clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, []);

  const send = async () => {
    const m = text.trim();
    if (m.length < 1) return;
    setErr(false);
    setText("");
    setMsgs((x) => [...x, { role: "visitor", text: m, at: new Date().toISOString() }]);
    setTyping(true);
    setTimeout(() => setTyping(false), 30000); // safety: never spin forever
    if (!sidRef.current) {
      sidRef.current = "v" + ((window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID().replace(/-/g, "") : Date.now().toString(36) + Math.random().toString(36).slice(2, 14));
      localStorage.setItem(SID_KEY, sidRef.current);
    }
    try {
      const r = await fetch("/api/chat-message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: m, contact: contact.trim(), page: window.location.href, sid: sidRef.current }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      if (d.sid && d.sid !== sidRef.current) { sidRef.current = d.sid; localStorage.setItem(SID_KEY, d.sid); }
      if (typeof window.gtag === "function") window.gtag("event", "chat_message");
    } catch (e) { setTyping(false); setErr(true); }
  };

  const toggle = () => { setOpen((o) => { if (!o) { setUnread(false); if (typeof window.gtag === "function") window.gtag("event", "chat_open"); } return !o; }); };

  const reset = () => {
    sidRef.current = "";
    lastAtRef.current = "";
    try { localStorage.removeItem(SID_KEY); localStorage.removeItem(MSGS_KEY); } catch {}
    setMsgs([]); setTyping(false); setErr(false); setContact("");
  };

  return (
    <div className="fixed right-4 bottom-4 z-[60] font-sans">
      {open && (
        <div className="absolute right-0 bottom-[60px] w-[min(340px,calc(100vw-32px))] bg-white border border-black/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
          <div className="flex items-center justify-between bg-[#111111] text-white px-4 py-3">
            <strong className="font-display text-[15px]">{T.head}</strong>
            <div className="flex items-center gap-3">
              <button onClick={reset} aria-label={T.reset} title={T.reset} className="opacity-80 hover:opacity-100"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={() => setOpen(false)} aria-label="Close" className="opacity-80 hover:opacity-100"><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div ref={bodyRef} className="px-3 py-3 flex flex-col gap-2 overflow-y-auto" style={{ maxHeight: "46vh", minHeight: 120 }}>
            <div className="self-start max-w-[85%] bg-[#F1F1EF] text-[#333] text-[13.5px] rounded-2xl rounded-tl-sm px-3 py-2">{T.greet}</div>
            {msgs.map((m, i) => (
              <div key={i} className={(m.role === "visitor" ? "self-end bg-[#e8560d] text-white rounded-br-sm" : "self-start bg-[#F1F1EF] text-[#222] rounded-tl-sm") + " max-w-[85%] text-[13.5px] rounded-2xl px-3 py-2 whitespace-pre-wrap break-words"}>{m.text}</div>
            ))}
            {typing && (
              <div className="self-start bg-[#F1F1EF] rounded-2xl rounded-tl-sm px-3 py-2.5 flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#999] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#999] animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#999] animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            )}
            {err && <div className="self-center text-[12px] text-[#993c1d]">{T.err}</div>}
          </div>

          <div className="border-t border-black/10 p-2.5">
            {msgs.length === 0 && (
              <input
                type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder={T.contact}
                className="w-full border border-black/10 rounded-[10px] px-3 py-2 text-[13px] outline-none mb-2 focus:border-[#e8560d]"
              />
            )}
            <div className="flex items-end gap-2">
              <textarea
                rows={1} value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={T.msg}
                className="flex-1 border border-black/10 rounded-xl px-3 py-2 text-sm outline-none resize-none max-h-24 focus:border-[#e8560d]"
              />
              <button onClick={send} aria-label={T.send} className="shrink-0 bg-[#e8560d] text-white rounded-xl p-2.5 hover:brightness-110 active:scale-95 transition">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={toggle} aria-label="Chat"
        className="relative inline-flex items-center gap-2 bg-[#e8560d] text-white rounded-full px-4 py-3.5 font-extrabold shadow-[0_10px_30px_-8px_rgba(0,78,235,.6)] hover:brightness-110 hover:-translate-y-0.5 transition"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline text-sm">{T.btn}</span>
        {unread && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#ff6a1f] border-2 border-white" />}
      </button>
    </div>
  );
}
